import { remoteConfigGetter } from "./remoteConfig";

export type SebiDisclosureKind = "awareness" | "risk";

export interface SebiDisclosureMessage {
  titleEn: string;
  pointsEn: string[];
  titleHi: string;
  pointsHi: string[];
  sourceEn?: string;
  sourceHi?: string;
}

// Remote Config key holding start_date, schedule and both bilingual messages.
const SEBI_DISCLOSURE_INFO = "sebi_disclosure_info";
const RISK_DISCLOSURES_FIRST_LOGIN = "risk_disclosures_first_login";
const RISK_DISCLOSURES_DAY_LOGIN = "risk_disclosures_day_login";

const SEEN_DATE_KEY = "sebi_disclosure_seen_date";
const FIRST_LOGIN_SEEN_KEY = "risk_disclosures_first_login_seen";
const DAY_LOGIN_TIME_KEY = "risk_disclosures_day_login_time";
const DAY_LOGIN_WINDOW_MS = 18 * 60 * 60 * 1000;

const FALLBACK_START_DATE = "2026-11-01";
const FO_EXCHANGES = new Set(["NSE_FO", "BSE_FO", "MCX_FO"]);

const WEEKDAY_CODES: Record<string, number> = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };

const FALLBACK_SCHEDULE: Record<number, SebiDisclosureKind> = {
  0: "awareness",
  1: "awareness",
  2: "risk",
  3: "awareness",
  4: "risk",
  5: "awareness",
  6: "risk",
};

const FALLBACK_MESSAGES: Record<SebiDisclosureKind, SebiDisclosureMessage> = {
  awareness: {
    titleEn: "SEBI presents Samajh Se Investing Simple:",
    pointsEn: [
      "Understand Before You Invest",
      "Choose Regulated Investment Products",
      "Start Early, Stay Invested",
      "Beware of Investment Frauds",
    ],
    titleHi: "सेबी की पेशकश - समझ से इन्वेस्टिंग सिंपल:",
    pointsHi: [
      "निवेश करने से पहले समझें",
      "विनियमित (Regulated) निवेश उत्पादों का चयन करें",
      "जल्दी शुरुआत करें, लंबे समय तक निवेशित रहें",
      "निवेश धोखाधड़ी (Investment Frauds) से सावधान रहें",
    ],
  },
  risk: {
    titleEn: "Risk Disclosures on Derivatives:",
    pointsEn: [
      "9 out of 10 individual traders in equity Futures and Options Segment, incurred net losses.",
      "The loss makers expended an additional 28% of net trading losses as transaction costs.",
      "Those making net trading profits incurred between 15% to 50% of such profits as transaction cost.",
    ],
    sourceEn:
      '(Source: SEBI study dated January 25, 2023 on "Analysis of Profit and Loss of Individual Traders dealing in equity Futures and Options (F&O) Segment")',
    titleHi: "डेरिवेटिव्स (Derivatives) पर जोखिम प्रकटीकरण:",
    pointsHi: [
      "इक्विटी फ्यूचर्स और ऑप्शंस (F&O) सेगमेंट में 10 में से 9 व्यक्तिगत ट्रेडर्स को शुद्ध नुकसान हुआ।",
      "नुकसान उठाने वाले ट्रेडर्स ने अपने कुल शुद्ध घाटे के अलावा 28% अतिरिक्त राशि ट्रांजैक्शन कॉस्ट (लेन-देन लागत) के रूप में चुकाई।",
      "जिन ट्रेडर्स ने शुद्ध लाभ कमाया, उनकी कमाई का 15% से 50% हिस्सा ट्रांजैक्शन कॉस्ट में चला गया।",
    ],
    sourceHi:
      "(स्रोत: इक्विटी फ्यूचर्स एंड ऑप्शंस सेगमेंट में व्यक्तिगत ट्रेडर्स के लाभ और हानि विश्लेषण पर सेबी की अध्ययन रिपोर्ट, दिनांक 25 जनवरी 2023)",
  },
};

const readRoot = (): Record<string, any> => {
  try {
    const parsed = JSON.parse(remoteConfigGetter(SEBI_DISCLOSURE_INFO).asString() || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const localDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const parseStartDate = (raw: unknown): string => {
  if (typeof raw !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) return FALLBACK_START_DATE;
  return raw.trim();
};

const parseKind = (value: unknown): SebiDisclosureKind | null => {
  const kind = String(value).trim().toLowerCase();
  return kind === "awareness" || kind === "risk" ? kind : null;
};

// A weekday missing from a valid remote schedule shows nothing; an unusable schedule falls back whole.
const parseSchedule = (raw: unknown): Record<number, SebiDisclosureKind> => {
  if (!raw || typeof raw !== "object") return FALLBACK_SCHEDULE;
  const parsed: Record<number, SebiDisclosureKind> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([code, kindName]) => {
    const weekday = WEEKDAY_CODES[code.trim().toUpperCase()];
    const kind = parseKind(kindName);
    if (weekday !== undefined && kind) parsed[weekday] = kind;
  });
  return Object.keys(parsed).length ? parsed : FALLBACK_SCHEDULE;
};

const stringOr = (value: unknown, fallback?: string) =>
  typeof value === "string" && value.trim() ? value : fallback;

const pointsOr = (value: unknown, fallback: string[]) => {
  if (!Array.isArray(value)) return fallback;
  const points = value.map((e) => String(e).trim()).filter(Boolean);
  return points.length ? points : fallback;
};

export const isFnoEnabled = (exchanges?: string[] | null) =>
  !!exchanges?.some((exchange) => FO_EXCHANGES.has(exchange));

export const isScheduleActive = (date: Date = new Date()) =>
  localDateKey(date) >= parseStartDate(readRoot().start_date);

// Before start_date only F&O users see the (risk) disclosure; after it non-F&O users see awareness daily.
export const sebiDisclosureKindFor = (isFnoUser: boolean, date: Date = new Date()): SebiDisclosureKind | null => {
  if (!isScheduleActive(date)) return isFnoUser ? "risk" : null;
  if (!isFnoUser) return "awareness";
  return parseSchedule(readRoot().schedule)[date.getDay()] ?? null;
};

export const sebiDisclosureMessageFor = (kind: SebiDisclosureKind): SebiDisclosureMessage => {
  const fallback = FALLBACK_MESSAGES[kind];
  const json = readRoot().messages?.[kind];
  if (!json || typeof json !== "object") return fallback;
  return {
    titleEn: stringOr(json.title_en, fallback.titleEn)!,
    pointsEn: pointsOr(json.points_en, fallback.pointsEn),
    titleHi: stringOr(json.title_hi, fallback.titleHi)!,
    pointsHi: pointsOr(json.points_hi, fallback.pointsHi),
    sourceEn: stringOr(json.source_en, fallback.sourceEn),
    sourceHi: stringOr(json.source_hi, fallback.sourceHi),
  };
};

// Reads AND stamps, so only call it once the disclosure will actually be drawn.
export const isSebiDisclosureDueNow = (date: Date = new Date()): boolean => {
  try {
    if (isScheduleActive(date)) {
      const today = localDateKey(date);
      if (localStorage.getItem(SEEN_DATE_KEY) === today) return false;
      localStorage.setItem(SEEN_DATE_KEY, today);
      return true;
    }
    if (remoteConfigGetter(RISK_DISCLOSURES_FIRST_LOGIN).asBoolean()) {
      if (localStorage.getItem(FIRST_LOGIN_SEEN_KEY) === "true") return false;
      localStorage.setItem(FIRST_LOGIN_SEEN_KEY, "true");
      return true;
    }
    if (remoteConfigGetter(RISK_DISCLOSURES_DAY_LOGIN).asBoolean()) {
      const seenAt = Number(localStorage.getItem(DAY_LOGIN_TIME_KEY) || 0);
      if (seenAt && date.getTime() <= seenAt + DAY_LOGIN_WINDOW_MS) return false;
      localStorage.setItem(DAY_LOGIN_TIME_KEY, String(date.getTime()));
      return true;
    }
  } catch {
    // Storage blocked: fall through and show it rather than skip a mandated disclosure.
  }
  return true;
};
